import type { DeviceType } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * The Screens area's client half (SPEC §7): wire shapes, paths and the bulk
 * push runner, kept out of the page so the contract-level behaviour is
 * testable against a mocked API client.
 *
 * The DTOs MIRROR `apps/api/src/screens/screens.dto.ts` BY HAND. House style
 * would put both in `@gc/shared`; this work may not edit `packages/**`, so the
 * server file carries the canonical shapes and the lead replaces this mirror
 * with a re-export when they move. Same arrangement as the portal launch DTOs.
 */

export const SCREENS_PATH = '/screens';

export interface ScreenGroupMediaDto {
  groupId: string;
  groupName: string;
  mediaAssetId: string;
  mediaUrl: string;
  mediaFileName: string | null;
}

export interface ScreenDeviceDto {
  id: string;
  name: string;
  deviceType: DeviceType;
  /** For a TV-driven seat this means "the bridge is connected", not "an
   *  agent's socket is open" — a console seat has no agent of its own. */
  online: boolean;
  /** §7 TV control: the idle video plays on this seat's TV via the bridge. */
  drivesTv: boolean;
  /** The bridge's last word on the television itself; null = not heard yet. */
  tvHealth: { ok: boolean; error: string | null; at: string } | null;
  idleMediaAssetId: string | null;
  idleMediaUrl: string | null;
  idleMediaFileName: string | null;
  groupIdleMedia: ScreenGroupMediaDto | null;
  /** The game this PC is set up for (2026-09-24); null on none and on every
   *  console. */
  gameId: string | null;
  gameTitle: string | null;
  /** The lock screen plays the GAME's video — the own one is the fallback. */
  playsGameVideo: boolean;
}

/** One game in the "Set game…" chooser. */
export interface ScreenGameDto {
  id: string;
  title: string;
  isActive: boolean;
  /** Has its video (the trailer from Settings > Games). */
  hasVideo: boolean;
  /** Active PCs whose per-PC path for the game is marked installed. */
  installedDeviceIds: string[];
  /** Active PCs a venue account for the game was made for. */
  accountDeviceIds: string[];
}

export interface ScreenMediaAssetDto {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: string;
  url: string;
  createdAt: string;
  usedByDeviceCount: number;
  usedByGroupCount: number;
}

export interface ScreensResponse {
  devices: ScreenDeviceDto[];
  media: ScreenMediaAssetDto[];
  games: ScreenGameDto[];
}

/** Refusal codes the API answers with; each has a translation under screens.errors. */
export const SCREEN_ERROR_CODES = [
  'device_not_found',
  'media_not_found',
  'wrong_media_kind',
  'device_offline',
  'central_device_ip_missing',
  'media_unpushable',
  'bridge_offline',
  'no_tv_seats',
  // "Set game…" (2026-09-24); `device_not_found` above covers its third.
  'game_not_found',
  'device_not_pc',
] as const;
export type ScreenErrorCode = (typeof SCREEN_ERROR_CODES)[number];

/** screens.errors.<code>, or the generic fallback for anything unlisted. */
export function errorKeyFor(code: string | null): string {
  return code && (SCREEN_ERROR_CODES as readonly string[]).includes(code)
    ? `screens.errors.${code}`
    : 'screens.errors.generic';
}

/**
 * Set (or clear, with `null`) a device's own idle video. `null` goes over the
 * wire explicitly — the schema distinguishes "clear it" from "field absent".
 */
export function assignIdleMedia(deviceId: string, mediaAssetId: string | null): Promise<unknown> {
  return api.put(`${SCREENS_PATH}/devices/${deviceId}/idle-media`, { mediaAssetId });
}

export interface PushSuccess {
  deviceId: string;
  result: 'sent';
  assetId: string;
}

export function pushIdleMedia(deviceId: string): Promise<PushSuccess> {
  return api.post<PushSuccess>(`${SCREENS_PATH}/devices/${deviceId}/push`);
}

/** §7 "Play Ad" — the chosen video takes over every FREE TV seat now. */
export interface PlayAdResult {
  playedOn: string[];
  inSession: string[];
  /** False = assignments saved but the bridge is offline; the TVs pick the
   *  ad up when it reconnects. */
  pushed: boolean;
}

export function playAd(mediaAssetId: string): Promise<PlayAdResult> {
  return api.post<PlayAdResult>(`${SCREENS_PATH}/ad`, { mediaAssetId });
}

/** DELETE goes to the existing media endpoint; the guard messaging is ours. */
export function deleteIdleAsset(assetId: string): Promise<void> {
  return api.delete<void>(`/media/${assetId}`);
}

export function assetUsageCount(asset: ScreenMediaAssetDto): number {
  return asset.usedByDeviceCount + asset.usedByGroupCount;
}

export function onlineDevices(devices: readonly ScreenDeviceDto[]): ScreenDeviceDto[] {
  return devices.filter((device) => device.online);
}

/** One row of the bulk-push result list. */
export interface PushOutcome {
  deviceId: string;
  deviceName: string;
  ok: boolean;
  /** Refusal code when the API refused; null on success or a network failure. */
  code: string | null;
  message: string | null;
}

/**
 * Push to each device IN TURN, never stopping at a failure: the point of the
 * result list is that staff see every screen's answer, and one offline PC
 * aborting the rest would hide the other nineteen. Sequential on purpose —
 * each push streams a video download to a machine, and twenty at once is a
 * self-inflicted spike on the venue's one server.
 */
export async function pushToDevices(
  devices: readonly Pick<ScreenDeviceDto, 'id' | 'name'>[],
): Promise<PushOutcome[]> {
  const outcomes: PushOutcome[] = [];
  for (const device of devices) {
    try {
      await pushIdleMedia(device.id);
      outcomes.push({
        deviceId: device.id,
        deviceName: device.name,
        ok: true,
        code: null,
        message: null,
      });
    } catch (caught) {
      outcomes.push({
        deviceId: device.id,
        deviceName: device.name,
        ok: false,
        code: caught instanceof ApiError ? caught.code : null,
        message: caught instanceof Error ? caught.message : null,
      });
    }
  }
  return outcomes;
}

/** Bytes (decimal string, bigint column) -> "12.5 MB" for the library pane. */
export function formatMegabytes(sizeBytes: string, locale: string): string {
  const bytes = Number(sizeBytes);
  if (!Number.isFinite(bytes)) return '—';
  const megabytes = bytes / (1024 * 1024);
  return `${new Intl.NumberFormat(locale, {
    maximumFractionDigits: 1,
    numberingSystem: 'latn',
  }).format(megabytes)} MB`;
}

/* -------------------------------------------------------------------------- */
/* Set game… (owner, 2026-09-24)                                              */
/* -------------------------------------------------------------------------- */

/**
 * "for each game i can set a video that will play on all the other PC's that
 * are the same game" — the owner, 2026-09-24. Staff tick PCs and choose the
 * game they are set up for; each PC's lock screen then plays that game's
 * video (its trailer, from Settings > Games) on its next two-second poll.
 *
 * The request is `devices.manage`, not this page's `media.manage`, so the page
 * offers the action only to employees who hold it. All or nothing: one PC the
 * server refuses (gone, or not a PC) refuses the lot.
 */
export const ASSIGN_GAME_PATH = '/devices/assign-game';

/** `null` is "No game" and goes over the wire explicitly — absent is refused. */
export function assignDeviceGame(
  deviceIds: readonly string[],
  gameId: string | null,
): Promise<{ updated: number }> {
  return api.post<{ updated: number }>(ASSIGN_GAME_PATH, { deviceIds: [...deviceIds], gameId });
}

/** The machines "Set game…" may tick: PCs only. A PS5's video plays on its
 *  TV and stays a Screens choice; the server refuses a console anyway. */
export function gamePcs(devices: readonly ScreenDeviceDto[]): ScreenDeviceDto[] {
  return devices.filter((device) => device.deviceType === 'PC');
}

/**
 * The games the chooser lists: every active game, plus a hidden one some PC
 * is still set to — so the chooser never reads as if that PC had no game.
 */
export function chooserGames(
  games: readonly ScreenGameDto[],
  devices: readonly ScreenDeviceDto[],
): ScreenGameDto[] {
  const inUse = new Set(devices.flatMap((device) => (device.gameId ? [device.gameId] : [])));
  return games.filter((game) => game.isActive || inUse.has(game.id));
}

/**
 * A helper's PCs, as ids the grid can tick: only PCs on this page, in grid
 * order. The server already filters to active PCs; this keeps a stale list
 * from ticking something the grid no longer shows.
 */
export function helperSelection(
  helperIds: readonly string[],
  devices: readonly ScreenDeviceDto[],
): string[] {
  const wanted = new Set(helperIds);
  return gamePcs(devices)
    .filter((device) => wanted.has(device.id))
    .map((device) => device.id);
}

/**
 * The ticked ids that are still PCs on the grid, in grid order — what Apply
 * sends. A tick on a machine that vanished on reload is dropped here rather
 * than sent to be refused as `device_not_found` for the whole batch.
 */
export function tickedPcIds(
  ticked: ReadonlySet<string>,
  devices: readonly ScreenDeviceDto[],
): string[] {
  return gamePcs(devices)
    .filter((device) => ticked.has(device.id))
    .map((device) => device.id);
}

/** One tick flipped. A new set, so React sees the change. */
export function toggleTick(ticked: ReadonlySet<string>, deviceId: string): Set<string> {
  const next = new Set(ticked);
  if (next.has(deviceId)) next.delete(deviceId);
  else next.add(deviceId);
  return next;
}

/**
 * A helper's PCs ADDED to the ticks, never replacing them: the owner may have
 * ticked a few by hand first, and a helper that wiped those would lose work
 * they could not see go.
 */
export function addTicks(ticked: ReadonlySet<string>, deviceIds: readonly string[]): Set<string> {
  return new Set([...ticked, ...deviceIds]);
}

/**
 * What to tell staff once "Set game…" succeeded: every ticked PC now has that
 * game (or none), so the sentence counts the ticks — unless the server
 * changed nothing at all, which is its own answer rather than a claim that
 * PCs were "set".
 *
 * The count travels as i18next's `count`, so one PC reads "1 PC set to…" and
 * Arabic gets its dual and plural forms; a plain number read "1 PCs".
 */
export function setGameNotice(
  updated: number,
  pcs: number,
  game: Pick<ScreenGameDto, 'title'> | null,
): { key: string; params: Record<string, string | number> } {
  if (updated === 0) return { key: 'screens.setGame.unchanged', params: {} };
  return game
    ? { key: 'screens.setGame.done', params: { count: pcs, game: game.title } }
    : { key: 'screens.setGame.doneNone', params: { count: pcs } };
}
